/**
 * CorpTaxWidget (R2)
 * Shows estimated corporate tax liability for the current fiscal year.
 * Simple % of total billed — estimation only, not a full accounting system.
 *
 * Settings: /settings → Tax (corp_tax_rate, corp_tax_remittance_dates)
 */
import { useCorpTax } from '@/hooks/useDashboardReports';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { TrendingDown } from 'lucide-react';
import { formatDateOnly } from '@/lib/utils';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
}

function formatDate(isoDate: string): string {
  // fy_start is a DATE column. Parsing it with the bare Date constructor
  // treats it as UTC midnight, which renders one calendar day early west
  // of UTC (e.g. America/Edmonton). formatDateOnly parses the y/m/d
  // components directly so the displayed date always matches the stored
  // calendar date.
  return formatDateOnly(isoDate, { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatMMDD(mmdd: string): string {
  const [m, d] = mmdd.split('-');
  const dt = new Date(2000, parseInt(m) - 1, parseInt(d));
  return dt.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
}

export function CorpTaxWidget() {
  const { data, isLoading, error } = useCorpTax();

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="h-5 w-5" />
            Corp Tax Estimate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 animate-pulse">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-48" />
            <Skeleton className="h-4 w-40" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TrendingDown className="h-5 w-5" />
            Corp Tax Estimate
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Failed to load corp tax data</p>
        </CardContent>
      </Card>
    );
  }

  const rateConfigured = data.corp_tax_rate > 0;

  return (
    <Card data-testid="corp-tax-widget">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingDown className="h-5 w-5" />
          Corp Tax Estimate
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {rateConfigured ? (
          <>
            {/* Estimated tax */}
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(data.estimated_corp_tax)}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {data.corp_tax_rate}% of{' '}
                <span className="font-medium">{formatCurrency(data.total_billed)}</span>{' '}
                billed YTD
              </div>
            </div>

            {/* Fiscal year */}
            <div className="text-xs text-gray-600">
              <span className="font-medium">Fiscal year from:</span>{' '}
              {formatDate(data.fy_start)}
            </div>

            {/* Remittance dates */}
            {data.corp_tax_remittance_dates.length > 0 && (
              <div className="text-xs text-gray-600">
                <span className="font-medium">Remittance dates:</span>{' '}
                {data.corp_tax_remittance_dates.map(formatMMDD).join(', ')}
              </div>
            )}
          </>
        ) : (
          <>
            {/* v0.12.27 Item 7: drop the em-dash placeholder — read as a stray
                underline above the prompt copy when no rate is configured. */}
            <p className="text-sm text-muted-foreground">
              Set your corporate tax rate in{' '}
              <span className="font-medium">Settings → Financial</span> to see your estimate.
            </p>
            <div className="text-xs text-gray-600">
              Total billed YTD:{' '}
              <span className="font-medium">{formatCurrency(data.total_billed)}</span>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
